import {
  KoButton,
  KoForm,
  KoModal,
  KoInput,
  KoTextArea,
  KoText,
  KoTitle,
  KoToast,
  KoTag,
  KoEmpty,
  KoSelect,
  KoFormBuilder
} from 'packages';
import KoIcon from '@kwapio/icons';
import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { add, remove } from 'shared/store/breadcrumbSlice';
import KoToolBar from 'shared/components/toolbar';
import { UAT_VALIDATION_CATEGORIES } from '../constants';
import ReleaseSubNav from './ReleaseSubNav';
import styles from './releases.module.scss';

const MOCK_UAT_ITEMS = [
  {
    itemId: 'UAT-001',
    ticketId: 'KWP-882',
    title: 'Dashboard Analytics Widgets',
    acceptanceCriteria: [
      { text: 'Revenue chart displays correct data for selected date range', met: true },
      { text: 'User activity heatmap updates in real-time', met: true },
      { text: 'Conversion funnel matches business intelligence reports', met: false },
      { text: 'Widgets are responsive on tablet and mobile', met: true }
    ],
    stakeholders: [
      { stakeholderId: 's1', name: 'Emily Davis', role: 'Product Manager', decision: 'signed_off', signedAt: '2026-02-22 16:30' },
      { stakeholderId: 's2', name: 'Michael Torres', role: 'Business Owner', decision: null }
    ]
  },
  {
    itemId: 'UAT-002',
    ticketId: 'KWP-891',
    title: 'Authentication Token Refresh Fix',
    acceptanceCriteria: [
      { text: 'No 401 errors during concurrent API calls', met: true },
      { text: 'Token refresh is transparent to the user', met: true },
      { text: 'Session persists across browser tabs', met: true }
    ],
    stakeholders: [
      { stakeholderId: 's1', name: 'Emily Davis', role: 'Product Manager', decision: 'signed_off', signedAt: '2026-02-23 10:00' },
      { stakeholderId: 's3', name: 'Lisa Chang', role: 'CX Lead', decision: 'signed_off', signedAt: '2026-02-23 11:30' }
    ]
  },
  {
    itemId: 'UAT-003',
    ticketId: 'KWP-912',
    title: 'Notification Preferences Panel',
    acceptanceCriteria: [
      { text: 'Users can toggle email notifications per category', met: false },
      { text: 'Push notification preferences sync across devices', met: false },
      { text: 'In-app notification settings are respected immediately', met: false }
    ],
    stakeholders: [
      { stakeholderId: 's2', name: 'Michael Torres', role: 'Business Owner', decision: null },
      { stakeholderId: 's4', name: 'David Wilson', role: 'External Stakeholder', decision: 'rejected', feedback: 'Push notifications still arrive after being disabled in settings.' }
    ]
  }
];

function UATSignoff() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { releaseId, workspaceId } = useParams();

  const [uatItems, setUATItems] = useState([]);
  const [isOpenInvite, setOpenInvite] = useState(false);
  const [feedbackMap, setFeedbackMap] = useState({});
  const [inviteForm] = KoForm.useForm();

  const releaseBasePath = workspaceId
    ? `/workspace/${workspaceId}/development/release`
    : '/release';

  useEffect(() => {
    dispatch(remove());
    dispatch(add([
      { title: 'Home', path: 'home/myoverview' },
      { title: 'Releases', path: releaseBasePath },
      { title: 'UAT Sign-off' }
    ]));
    setUATItems(MOCK_UAT_ITEMS);
  }, [workspaceId]);

  const signOff = (itemId, stakeholderId) => {
    setUATItems((prev) => prev.map((item) => {
      if (item.itemId !== itemId) return item;
      return {
        ...item,
        stakeholders: item.stakeholders.map((s) =>
          s.stakeholderId === stakeholderId
            ? { ...s, decision: 'signed_off', signedAt: new Date().toLocaleString() }
            : s
        )
      };
    }));
    KoToast.success('Signed off successfully!');
  };

  const reject = (itemId, stakeholderId) => {
    const feedbackKey = `${itemId}_${stakeholderId}`;
    const feedback = feedbackMap[feedbackKey];
    if (!feedback?.trim()) {
      KoToast.error('Please provide rejection feedback before rejecting.');
      return;
    }
    setUATItems((prev) => prev.map((item) => {
      if (item.itemId !== itemId) return item;
      return {
        ...item,
        stakeholders: item.stakeholders.map((s) =>
          s.stakeholderId === stakeholderId ? { ...s, decision: 'rejected', feedback } : s
        )
      };
    }));
    KoToast.success('UAT rejected — ticket moved back to dev queue.');
  };

  const handleInvite = (values) => {
    KoToast.success('Invite sent — UAT access link emailed!');
    setOpenInvite(false);
    inviteForm.resetFields();
  };

  const allSignedOff = uatItems.length > 0 &&
    uatItems.every((item) => item.stakeholders?.every((s) => s.decision === 'signed_off'));

  const signedCount = uatItems.reduce((acc, item) =>
    acc + (item.stakeholders?.filter((s) => s.decision === 'signed_off').length || 0), 0);
  const totalCount = uatItems.reduce((acc, item) =>
    acc + (item.stakeholders?.length || 0), 0);

  return (
    <div className='tableCard'>
      <ReleaseSubNav activeKey='uat' />

      <KoToolBar
        title='UAT Sign-off'
        search={{ visible: false }}
        filter={{ visible: false, options: [] }}
        buttonList={[
          { label: 'Invite Stakeholder', type: 'primary', icon: <KoIcon color='#FFF' name='PlusOutlined' width={18} height={18} />, onClick: () => setOpenInvite(true) }
        ]}
      />

      <div className={styles.flowBanner}>
        <KoIcon name='FileDoneOutlined' width={14} height={14} />
        <KoText text='Step 3: UAT Sign-off — All stakeholders must approve before Release Gate' />
      </div>

      <div className={styles.uatCategories}>
        <KoText className={styles.sectionLabel} text='UAT VALIDATION SCOPE' />
        <div className={styles.uatCategoryGrid}>
          {UAT_VALIDATION_CATEGORIES.map(cat => (
            <div key={cat.key} className={styles.uatCategoryChip}>
              <KoIcon name='CheckCircleOutlined' width={14} height={14} color='var(--palette-primary-main)' />
              <KoText text={cat.label} />
            </div>
          ))}
        </div>
      </div>

      <div className={styles.progressBar}>
        <div className={styles.progressInfo}>
          <KoText text={`UAT Progress: ${signedCount} of ${totalCount} sign-offs complete`} />
          <KoText text={`${Math.round((signedCount / (totalCount || 1)) * 100)}%`} className={styles.progressPct} />
        </div>
        <div className={styles.progressTrack}>
          <div className={styles.progressFill} style={{ width: `${(signedCount / (totalCount || 1)) * 100}%` }} />
        </div>
      </div>

      {uatItems.length === 0 && (
        <KoEmpty description='No UAT items yet. Link tickets and complete QA first.' />
      )}

      {uatItems.map((item) => {
        const itemSignedCount = item.stakeholders?.filter((s) => s.decision === 'signed_off').length || 0;
        const itemTotal = item.stakeholders?.length || 0;
        const itemAllSigned = itemSignedCount === itemTotal && itemTotal > 0;

        return (
          <div key={item.itemId} className={styles.uatCard}>
            <div className={styles.uatCardHeader}>
              <div className={styles.uatCardTitle}>
                <span className={styles.ticketChip}>{item.ticketId}</span>
                <KoTitle level={5} text={item.title} />
                <KoTag color={itemAllSigned ? 'success' : 'warning'}>
                  {itemAllSigned ? 'All Signed Off' : 'Awaiting Sign-off'}
                </KoTag>
              </div>
            </div>

            <div className={styles.uatCardBody}>
              <div className={styles.criteria}>
                <KoText className={styles.sectionLabel} text='ACCEPTANCE CRITERIA' />
                {(item.acceptanceCriteria || []).map((ac, i) => (
                  <div key={i} className={styles.criteriaRow}>
                    <KoIcon
                      name={ac.met ? 'CheckCircleOutlined' : 'ClockCircleOutlined'}
                      width={14} height={14}
                      color={ac.met ? 'var(--palette-success-main)' : 'var(--palette-warning-main)'}
                    />
                    <KoText text={ac.text} />
                  </div>
                ))}
              </div>

              <div className={styles.stakeholders}>
                <KoText className={styles.sectionLabel} text='STAKEHOLDER SIGN-OFFS' />
                {(item.stakeholders || []).map((s) => {
                  const feedbackKey = `${item.itemId}_${s.stakeholderId}`;
                  return (
                    <div key={s.stakeholderId} className={styles.stakeholderRow}>
                      <div className={styles.stakeholderInfo}>
                        <div className={styles.avatarCircle}>{s.name?.charAt(0)?.toUpperCase()}</div>
                        <div>
                          <KoText className={styles.stakeholderName} text={s.name} />
                          <KoText className={styles.stakeholderRole} text={s.role} />
                        </div>
                      </div>

                      {s.decision === 'signed_off' && (
                        <div className={styles.signoffResult}>
                          <KoTag color='success'>Signed Off</KoTag>
                          {s.signedAt && <KoText className={styles.signoffTime} text={s.signedAt} />}
                        </div>
                      )}
                      {s.decision === 'rejected' && (
                        <div className={styles.signoffResult}>
                          <KoTag color='error'>Rejected</KoTag>
                          {s.feedback && <KoText className={styles.rejectionFeedback} text={s.feedback} />}
                        </div>
                      )}
                      {!s.decision && (
                        <div className={styles.stakeholderActions}>
                          <KoTextArea
                            rows={2}
                            placeholder='Feedback or rejection reason...'
                            value={feedbackMap[feedbackKey] || ''}
                            onChange={(e) => setFeedbackMap((prev) => ({ ...prev, [feedbackKey]: e.target.value }))}
                            className={styles.feedbackInput}
                          />
                          <div className={styles.actionBtns}>
                            <KoButton type='primary' label='Sign Off' onClick={() => signOff(item.itemId, s.stakeholderId)} />
                            <KoButton danger label='Reject' onClick={() => reject(item.itemId, s.stakeholderId)} />
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        );
      })}

      <div className={`${styles.deploySection} ${allSignedOff ? styles.deploySectionReady : ''}`}>
        {allSignedOff ? (
          <>
            <KoIcon name='CheckCircleOutlined' width={32} height={32} color='var(--palette-success-main)' />
            <KoTitle level={5} text='All UAT sign-offs complete — Ready to proceed to Release Gate!' />
            <KoButton type='primary' size='large' label='Proceed to Release Gate'
              icon={<KoIcon name='LockOutlined' color='#FFF' width={16} height={16} />}
              onClick={() => navigate(`${releaseBasePath}/${releaseId}/gate`)} />
          </>
        ) : (
          <>
            <KoIcon name='LockOutlined' width={28} height={28} color='var(--palette-text-secondary)' />
            <KoTitle level={5} text='Release Gate is locked' />
            <KoText text='Complete all UAT sign-offs to unlock the production release gate' />
          </>
        )}
      </div>

      <KoModal title='Invite Stakeholder to UAT' centered open={isOpenInvite}
        handleOk={() => setOpenInvite(false)} onClose={() => setOpenInvite(false)}
        width={'min(560px, 95vw)'}
        footer={[
          <KoButton key='cancel' label='Cancel' onClick={() => setOpenInvite(false)} />,
          <KoButton key='send' type='primary' label='Send Invite' onClick={() => inviteForm.submit()} />
        ]}
      >
        <InviteCrud form={inviteForm} handleFinish={handleInvite} />
      </KoModal>
    </div>
  );
}

function InviteCrud({ form, handleFinish }) {
  const RoleWidget = ({ value, onChange }) => (
    <KoSelect value={value} onChange={onChange} placeholder='Select role'
      options={[
        { label: 'Product Manager', value: 'pm' },
        { label: 'Business Owner', value: 'business_owner' },
        { label: 'CX Lead', value: 'cx_lead' },
        { label: 'External Stakeholder', value: 'external' }
      ]} />
  );

  const meta = {
    columns: 4, formItemLayout: null, colon: true,
    fields: [
      { key: 'name', label: 'Full Name', colSpan: 2, widget: KoInput, widgetProps: { placeholder: 'Alice Park' } },
      { key: 'email', label: 'Email', colSpan: 2, widget: KoInput, widgetProps: { type: 'email', placeholder: 'alice@company.com' } },
      { key: 'role', label: 'Role', colSpan: 4, widget: RoleWidget, forwardRef: true },
      { key: 'ticketIds', label: 'Tickets to Review', colSpan: 4, widget: KoInput, widgetProps: { placeholder: 'KWP-882, KWP-891', style: { fontFamily: 'monospace' } } }
    ]
  };

  return (
    <KoForm form={form} layout='vertical' onFinish={handleFinish}>
      <KoFormBuilder form={form} meta={meta} />
    </KoForm>
  );
}

export default UATSignoff;
